import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';

import connect from 'components/lib/connect';
import ForgotPassword from 'models/location/forgot_password';
import { default as PattyWithCheese } from 'components/lib/icons/hamburger';
import Inbox from 'models/location/inbox';
import NavigationMenu from 'components/root/navigation_menu';
import useDevice from 'components/hooks/use_device';
import { Device } from 'scripts/lib/device_detector';
import OutsideClickHandler from 'components/common/utilities/outside_click_handler';
import UserActivation from 'models/location/user_activation';
import UserLogin from 'models/location/user_login';
import Floorboard from 'models/location/floorboard';
import UserResetPassword from 'models/location/user_reset_password';
import useIsPaymentOverdueBannerVisible from 'components/hooks/use_is_payment_overdue_banner_visible';

const NO_HAMBURGER_LOCATIONS = [ForgotPassword, UserActivation, UserLogin, UserResetPassword, Floorboard];

export function NavigationBase({
  hasConversationGeneral,
  hasCurrentAgent,
  isAnswersAdminEnabled,
  isReportsEnabled,
  isLoginEnabled,
  isInbox,
  locationNeedsHamburger,

  isOpen,
  setIsOpen,
}) {
  const isPhone = useDevice() === Device.Phone;
  const toggle = useCallback(
    evt => {
      evt.preventDefault();
      setIsOpen(!isOpen);
    },
    [setIsOpen, isOpen]
  );
  const close = useCallback(() => setIsOpen(false), [setIsOpen]);
  // On phone the hamburger only appears on the home/inbox screen; every other
  // page uses a back button in the top bar instead.
  const isHidden =
    !hasCurrentAgent ||
    !locationNeedsHamburger ||
    (isPhone && !isInbox) ||
    (!hasConversationGeneral && !isAnswersAdminEnabled && !isReportsEnabled && !isLoginEnabled);

  // The open state lives in RootContent, which paints the overlay from it. Nothing but this menu
  // can close it, so when the menu goes without being closed, as when a call or a session's end
  // moves the agent to a page with no hamburger, it closes itself on the way out or the overlay
  // would stay and swallow every click.
  useEffect(() => {
    if (isHidden && isOpen) setIsOpen(false);
  }, [isHidden, isOpen, setIsOpen]);

  if (isHidden) {
    return null;
  }
  return (
    <OutsideClickHandler onClickOutside={close}>
      <span>
        <NavigationMenu onClose={close} />
        <Cheeseburger isOpen={isOpen} toggleOpen={toggle} />
      </span>
    </OutsideClickHandler>
  );
}

NavigationBase.propTypes = {
  hasConversationGeneral: PropTypes.bool,
  hasCurrentAgent: PropTypes.bool,
  isAnswersAdminEnabled: PropTypes.bool,
  isInbox: PropTypes.bool,
  isLoginEnabled: PropTypes.bool,
  isReportsEnabled: PropTypes.bool,
  locationNeedsHamburger: PropTypes.bool,

  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func.isRequired,
};

const Navigation = connect(mapStateToProps)(NavigationBase);
export default Navigation;

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const currentLocation = getProvider('currentLocation').get();

  return {
    hasConversationGeneral: isFeatureEnabled('conversation:general'),
    hasCurrentAgent: !!getProvider('currentAgent').get(),
    isAnswersAdminEnabled: isFeatureEnabled('answers:admin'),
    isInbox: currentLocation instanceof Inbox,
    isLoginEnabled: isFeatureEnabled('login'),
    isReportsEnabled: isFeatureEnabled('reports:read'),
    locationNeedsHamburger: !_.find(NO_HAMBURGER_LOCATIONS, location => currentLocation instanceof location),
  };
}

export function Cheeseburger({ isOpen, toggleOpen }) {
  const isPaymentOverdueBannerVisible = useIsPaymentOverdueBannerVisible();

  return (
    <Bun $isPaymentOverdueBannerVisible={isPaymentOverdueBannerVisible} isOpen={isOpen}>
      <PattyWithCheese isOpen={isOpen} onClick={toggleOpen} />
    </Bun>
  );
}

const Bun = styled.div`
  align-items: center;
  display: flex;
  left: 20px;
  position: fixed;
  top: ${p => (p.$isPaymentOverdueBannerVisible ? '58px' : '22px')};
  transition: transform 0.38s ease;
  z-index: 10;

  ${p => p.isOpen && toastedBun};
`;

const toastedBun = css`
  top: 16px;
  width: 17px;
  opacity: 0.7;
  transform: translate3d(260px, 0, 0);
`;
